import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Avatar, Badge, cx } from './ui.jsx';
import { jobStep, lumperStep } from '../lib/plain.js';

export function PageHead({ title, subtitle, icon: Icon, actions, breadcrumb, children }) {
  return (
    <div className="page-head">
      <div className="grow" style={{ minWidth: 0 }}>
        {breadcrumb ? (
          <div className="row gap-6 label mb-8" style={{ flexWrap: 'wrap' }}>
            {breadcrumb.map((crumb, i) => (
              <span key={crumb.label ?? i} className="row gap-6">
                {i ? <ChevronRight size={11} strokeWidth={2.4} className="dimmer" /> : null}
                {crumb.to ? (
                  <Link to={crumb.to} className="dim" style={{ letterSpacing: 'inherit' }}>{crumb.label}</Link>
                ) : (
                  <span className="dimmer">{crumb.label}</span>
                )}
              </span>
            ))}
          </div>
        ) : null}
        <h1>
          {Icon ? <Icon size={24} strokeWidth={2} style={{ color: 'var(--amber)' }} /> : null}
          {title}
        </h1>
        {subtitle ? <p>{subtitle}</p> : null}
        {children}
      </div>
      {actions ? <div className="row gap-8 wrap">{actions}</div> : null}
    </div>
  );
}

export const JobStatusBadge = ({ status, size, dot = true, short = false }) => {
  const step = jobStep(status);
  return (
    <Badge tone={step.tone} size={size} dot={dot} live={status === 'in_progress'}>
      {short ? step.short : step.plain}
    </Badge>
  );
};

export const LumperStatusBadge = ({ status, size, short = false }) => {
  const step = lumperStep(status);
  // What the word means, on hover: the roster has seven states and nobody
  // should have to learn them by asking.
  return (
    <Badge tone={step.tone} size={size} title={step.means || step.plain}>
      {short ? step.short : step.plain}
    </Badge>
  );
};

/** A small grey line explaining a number or heading in one sentence. */
export const Helper = ({ children, className }) => (
  <div className={cx('helper', className)}>{children}</div>
);

/** A sentence where a stack of metrics would be too much. */
export const Says = ({ children, tone, icon: Icon, action, className }) => (
  <div className={cx('says', className)} data-tone={tone}>
    {Icon ? <Icon size={16} strokeWidth={2.1} className="says-icon" /> : null}
    <div className="grow">{children}</div>
    {action}
  </div>
);

export const LumperName = ({ id, name, tone, size = 'sm', subtitle, link = true }) => {
  const body = (
    <span className="row gap-8" style={{ minWidth: 0 }}>
      <Avatar name={name} tone={tone || 'amber'} size={size} />
      <span className="grow truncate">
        <span className="truncate" style={{ display: 'block' }}>{name || 'Unassigned'}</span>
        {subtitle ? <span className="dim truncate" style={{ display: 'block', fontSize: 'var(--fs-xs)' }}>{subtitle}</span> : null}
      </span>
    </span>
  );
  if (!link || !id || !name) return body;
  return <Link to={`/app/lumpers/${id}`} style={{ minWidth: 0, display: 'block' }}>{body}</Link>;
};

export function PriorityFlag({ priority }) {
  if (priority === 'hot') return <Badge tone="red" size="sm" dot>Rush</Badge>;
  if (priority === 'low') return <Badge tone="muted" size="sm">Not urgent</Badge>;
  return null;
}

export function SectionTitle({ label, action, children }) {
  return (
    <div className="row between gap-12 mb-12">
      <div className="row gap-8">
        <span className="label">{label}</span>
        {children}
      </div>
      {action}
    </div>
  );
}

export function DataPoint({ label, value, tone, hint, className }) {
  return (
    <div className={cx('col gap-2', className)} data-tone={tone}>
      <span className="label">{label}</span>
      <span className="num" style={{ fontSize: 'var(--fs-lg)', fontWeight: 620, color: tone ? 'var(--tone)' : undefined }}>{value}</span>
      {hint ? <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{hint}</span> : null}
    </div>
  );
}

export function SparklineRow({ label, value, children, tone = 'amber' }) {
  return (
    <div className="row between gap-12" style={{ padding: '7px 0' }}>
      <span className="row gap-8 truncate">
        <span className="tone-dot" data-tone={tone} />
        <span className="truncate">{label}</span>
      </span>
      <span className="row gap-10">
        {children}
        <span className="num" style={{ fontSize: 'var(--fs-sm)', color: 'var(--fg-2)' }}>{value}</span>
      </span>
    </div>
  );
}

export const RiskList = ({ items = [] }) => (
  <div className="col">
    {items.map((item, i) => (
      <div key={i} className="row between gap-12" style={{ padding: '8px 0', borderBottom: i === items.length - 1 ? 'none' : '1px solid var(--line)' }}>
        {item}
      </div>
    ))}
  </div>
);
